'use client'

import { useEffect, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useBillingOverviewQuery } from '@/hooks/queries/billing-overview'
import {
  BILLING_REDIRECT_QUERY_PARAM,
  BILLING_REDIRECT_BANNER_SUPPRESS_MS,
} from '@/lib/billing/redirect-marker'
import { PAYMENT_PROVIDERS } from '@/lib/billing/constants'

export function LowBalanceBanner() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const { data, dataUpdatedAt, refresh } = useBillingOverviewQuery()

  /**
   * Back from a Stripe flow the webhook is still racing the redirect and the shared
   * cache holds a pre-payment snapshot, so a stale «balance depleted» would scare a
   * user who just paid. Stay hidden until a fetch that succeeded after the window.
   */
  const [releaseAt] = useState(() =>
    searchParams?.get(BILLING_REDIRECT_QUERY_PARAM) === '1'
      ? Date.now() + BILLING_REDIRECT_BANNER_SUPPRESS_MS
      : 0,
  )

  useEffect(() => {
    if (!releaseAt) return
    const timer = setTimeout(() => void refresh(), BILLING_REDIRECT_BANNER_SUPPRESS_MS)
    return () => clearTimeout(timer)
  }, [releaseAt, refresh])

  if (!data || dataUpdatedAt < releaseAt) return null

  const capScope = data.spendingCap.reached ? data.spendingCap.scope ?? 'daily' : null
  const isDepleted = data.balance.isDepleted
  if (!capScope && !isDepleted && !data.balance.isLow) return null

  const provider = data.subscription?.payment_provider ?? null
  const isManual = provider === PAYMENT_PROVIDERS.MANUAL
  const isBillingPage = searchParams?.get('page') === 'billing'
  // Shopify top-ups are hidden (App Pricing blocks one-time charges; deferred past
  // review), so never prompt a Shopify merchant to "top up" — there's no entry point.
  const canTopup = !isManual && provider !== PAYMENT_PROVIDERS.SHOPIFY

  const depletedText = isManual
    ? 'Your balance is depleted. Contact the platform team to restore service.'
    : canTopup
      ? 'Your balance is depleted. Top up to continue using AI and voice services.'
      : 'Your balance is depleted. AI and voice services are paused.'

  const lowText = isManual
    ? 'Your balance is running low. Contact the platform team if you need additional balance.'
    : canTopup
      ? 'Your balance is running low. Consider topping up soon.'
      : 'Your balance is running low.'

  // Cap-reached is the most urgent (agents are already refused) and takes the banner.
  const capText = capScope
    ? `AI features are paused — you've reached your ${capScope} spending limit. They resume when the ${capScope === 'monthly' ? 'month' : 'day'} resets, or raise the limit in Billing settings.`
    : null

  const critical = capScope !== null || isDepleted

  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm ${critical ? 'bg-destructive/10 text-destructive' : 'bg-yellow-50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200'}`}>
      <div className="flex min-w-0 items-center gap-2">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        <span>{capText ?? (isDepleted ? depletedText : lowText)}</span>
      </div>
      {canTopup && !isBillingPage && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 bg-background text-foreground"
          onClick={() => router.push('/?page=billing')}
        >
          Go to Billing
        </Button>
      )}
    </div>
  )
}
